import { useCallback, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { PersonDto, PersonTypeEnum } from 'api/actor';
import { ActorForm } from 'features/new-actor/components/ActorForm';
import { ActorLayoutVariant } from 'features/new-actor/enums/actorLayoutVariant';
import { ActorType } from 'features/new-actor/enums/actorType';
import { naturalPersonFormConfig } from 'features/new-actor/form/naturalPersonFormConfig';
import { legalEntityFormConfig } from 'features/new-actor/form/optim/legalEntityFormConfig';
import {
  ActorFormValues,
  useSubmitActorForm,
} from 'features/new-actor/hooks/optim/useSubmitActorForm';
import { PersonDetails } from 'features/subscription-process/registration-process/components/PersonDetails';
import { CenteredDashboardContent } from 'features/dashboard/DashboardContent';
import { Sizes } from 'components/forms/NumberSelector';
import { StepPage } from 'features/subscription-process/navigation/Steps/StepPage';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { GroupContractScope } from '../api/groupContractSubscribersApi';
import { EligibleSubscriberSearch } from './EligibleSubscriberSearch';

interface SubscriberPickerProps {
  /** Contrat de groupe en cours — borne la recherche des souscripteurs éligibles. */
  contractId: string;
  /** Portée du contrat — conditionne la liste éligible et la création à la volée. */
  scope: GroupContractScope;
  /**
   * Person already attached to the contract: when set, the picker shows a READ-ONLY
   * summary (PersonDetails) — no change possible. When null, opens on search.
   */
  initialPerson?: PersonDto | null;
  /** Called once the user confirms (selection in search, form save, or "Suivant" on summary). */
  onPersonReady: (personId: string) => Promise<void> | void;
  /** True while parent is processing onPersonReady. */
  isSubmitting?: boolean;
  /**
   * Plateforme cible. Quand renseigné, toute Personne créée via le formulaire est rattachée
   * à cette OU (Personne.PlateformeId). Utilisé pour les contrats de portée PersonneMorale.
   */
  plateformeId?: string;
}

type Mode = 'search' | 'form';

const FORM_CONFIG = {
  [ActorType.Natural]: naturalPersonFormConfig,
  [ActorType.Legal]: legalEntityFormConfig,
};

/**
 * Garantit que la PersonDto passée à <PersonDetails> a son discriminator `type`.
 * Le polymorphic serialization du back peut, dans certains cas, ne pas l'émettre
 * → on infère depuis la structure pour éviter le fallback "Type non reconnu".
 */
const ensurePersonType = (raw: any, hint?: PersonTypeEnum): PersonDto => {
  if (raw?.type === 'morale' || raw?.type === 'physique') return raw as PersonDto;
  const inferredType: 'morale' | 'physique' =
    hint === PersonTypeEnum.LegalEntity ? 'morale'
    : hint === PersonTypeEnum.NaturalPerson ? 'physique'
    : ('companyName' in raw || 'legalFormId' in raw || 'registrationNumber' in raw) ? 'morale'
    : 'physique';
  return { ...raw, type: inferredType } as PersonDto;
};

export const SubscriberPicker = ({
  contractId,
  scope,
  initialPerson = null,
  onPersonReady,
  isSubmitting = false,
  plateformeId,
}: SubscriberPickerProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common, NsKey.ActorForm]);

  // La création de souscripteur à la volée n'est permise que pour la portée PersonneMorale.
  // Portée Plateforme → souscripteur strictement issu du portefeuille partagé.
  const allowCreate = scope === GroupContractScope.PersonneMorale;

  const [mode, setMode] = useState<Mode>('search');
  const [isFormValid, setIsFormValid] = useState(false);
  const formikRef = useRef<FormikProps<ActorFormValues>>(null);

  // Après soumission (create) : useSubmitActorForm renseigne values.id puis appelle postSubmit.
  const handlePostSubmit = useCallback(
    async (values: Partial<ActorFormValues>) => {
      if (!values?.id) return;
      await onPersonReady(values.id);
    },
    [onPersonReady],
  );

  const { handleSubmit, editConfig } = useSubmitActorForm({
    person: undefined,
    postSubmit: handlePostSubmit,
    plateformeId,
    formikRef,
  });

  const handleStartCreate = useCallback(() => setMode('form'), []);
  const handleBackToSearch = useCallback(() => setMode('search'), []);
  const handleSubmitForm = useCallback(() => formikRef.current?.submitForm(), []);

  const handleConfirmExisting = useCallback(async () => {
    if (!initialPerson?.id) return;
    await onPersonReady(initialPerson.id);
  }, [initialPerson, onPersonReady]);

  // -------- VUE RÉSUMÉ : revisite, souscripteur déjà attaché, lecture seule --------
  if (initialPerson) {
    return (
      <CenteredDashboardContent>
        <StepPage
          containerSize={Sizes['5xl']}
          nextConfig={{
            label: isSubmitting
              ? t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVING')
              : t('GROUP_CONTRACT_PROCESS.ACTIONS.NEXT'),
            onClick: handleConfirmExisting,
            disabled: isSubmitting,
          }}
        >
          <PersonDetails person={ensurePersonType(initialPerson)} />
        </StepPage>
      </CenteredDashboardContent>
    );
  }

  // -------- VUE FORMULAIRE : création d'un nouveau souscripteur (portée PersonneMorale) --------
  if (mode === 'form') {
    return (
      <CenteredDashboardContent>
        <StepPage
          containerSize={Sizes['5xl']}
          prevConfig={{
            label: t('GROUP_CONTRACT_PROCESS.ACTIONS.CANCEL'),
            onClick: handleBackToSearch,
          }}
          nextConfig={{
            label: isSubmitting
              ? t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVING')
              : t('GROUP_CONTRACT_PROCESS.ACTIONS.NEXT'),
            onClick: handleSubmitForm,
            disabled: !isFormValid || isSubmitting,
          }}
        >
          <ActorForm<ActorFormValues>
            layoutVariant={ActorLayoutVariant.UpiActor}
            formConfig={FORM_CONFIG}
            formikRef={formikRef}
            editConfig={editConfig}
            onValidationChange={setIsFormValid}
            onSubmit={handleSubmit}
          />
        </StepPage>
      </CenteredDashboardContent>
    );
  }

  // -------- VUE RECHERCHE : liste bornée des souscripteurs éligibles --------
  return (
    <EligibleSubscriberSearch
      contractId={contractId}
      onSelectPerson={onPersonReady}
      isSubmitting={isSubmitting}
      showAddForm={allowCreate ? handleStartCreate : undefined}
      title={t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_STEP.SEARCH_TITLE')}
      searchPlaceholder={t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_STEP.PLACEHOLDER')}
      createLabel={t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_STEP.CREATE_NEW')}
    />
  );
};
